import type { ScholarshipFormData, FormErrors } from './types';
import {
  MAX_FILE_SIZE,
  MIN_FILE_SIZE,
  GOVERNORATE_LABELS,
} from './constants';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeArabicDigits(str: string): string {
  const arabicIndic = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return str.replace(/[٠-٩]/g, (d) => `${arabicIndic.indexOf(d)}`);
}

export function cleanPhone(raw: string): string {
  let cleaned = normalizeArabicDigits(raw).replace(/[\s\-()]/g, '');
  if (cleaned.startsWith('+20')) {
    cleaned = '0' + cleaned.slice(3);
  } else if (cleaned.startsWith('0020')) {
    cleaned = '0' + cleaned.slice(4);
  }
  return cleaned;
}

export function calculateAge(dobString: string): number | null {
  if (!dobString) return null;
  const birthDate = new Date(dobString);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function validateFile(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase();
  const allowedExts = ['pdf', 'jpg', 'jpeg', 'png'];
  const allowedMimes = ['application/pdf', 'image/jpeg', 'image/png'];

  if (!ext || !allowedExts.includes(ext) || (file.type && !allowedMimes.includes(file.type))) {
    return 'نوع الملف ده مش مدعوم. ابعت PDF أو JPG أو PNG.';
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'الملف كبير أوي. الحد الأقصى 20 ميجا.';
  }

  if (file.size < MIN_FILE_SIZE) {
    return 'الملف فاضي أو صغير أوي.';
  }

  return null;
}

export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateField(
  name: keyof ScholarshipFormData,
  value: string,
  allValues: ScholarshipFormData
): string | undefined {
  const trimmed = value.trim();

  switch (name) {
    case 'full_name':
      if (!trimmed) return 'اكتب اسمك بالكامل';
      if (trimmed.length < 3 || trimmed.length > 120) return 'الاسم لازم يكون من 3 لـ 120 حرف';
      if (!trimmed.includes(' ') || trimmed.split(/\s+/).filter(Boolean).length < 2) {
        return 'اكتب اسمك ثنائي على الأقل';
      }
      return undefined;

    case 'email':
      if (!trimmed) return 'اكتب بريدك الإلكتروني';
      if (trimmed.length > 255 || !EMAIL_REGEX.test(trimmed)) return 'البريد الإلكتروني مش مظبوط';
      return undefined;

    case 'phone':
      // Deliberately unvalidated: any phone format is accepted, including
      // international numbers and an empty value.
      return undefined;

    case 'date_of_birth': {
      if (!trimmed) return 'اختار تاريخ ميلادك';
      const age = calculateAge(trimmed);
      if (age === null) return 'تاريخ الميلاد مش مظبوط';
      if (age < 15 || age > 45) return 'السن لازم يكون من 15 لـ 45 سنة';
      return undefined;
    }

    case 'governorate':
      if (!trimmed) return 'اختار محافظتك';
      if (!GOVERNORATE_LABELS[trimmed]) return 'المحافظة دي مش موجودة في القايمة';
      return undefined;

    case 'education_level':
      if (!trimmed) return 'اختار مؤهلك الدراسي';
      return undefined;

    case 'school_name':
      if (
        allValues.education_level === 'high_school_student' ||
        allValues.education_level === 'high_school_graduate'
      ) {
        if (!trimmed || trimmed.length < 2 || trimmed.length > 200) {
          return 'اكتب اسم مدرستك';
        }
      }
      return undefined;

    case 'university_name':
      if (
        allValues.education_level === 'university_student' ||
        allValues.education_level === 'university_graduate'
      ) {
        if (!trimmed || trimmed.length < 2 || trimmed.length > 200) {
          return 'اكتب اسم جامعتك';
        }
      }
      return undefined;

    case 'faculty':
      if (
        allValues.education_level === 'university_student' ||
        allValues.education_level === 'university_graduate'
      ) {
        if (!trimmed || trimmed.length < 2 || trimmed.length > 200) {
          return 'اكتب اسم كليتك';
        }
      }
      return undefined;

    case 'major':
      if (
        allValues.education_level === 'university_student' ||
        allValues.education_level === 'university_graduate'
      ) {
        if (trimmed.length > 200) {
          return 'التخصص طويل أوي';
        }
      }
      return undefined;

    case 'graduation_year': {
      if (!allValues.education_level) return undefined;
      const currentYear = new Date().getFullYear();
      const maxYear = currentYear + 8;
      const yr = parseInt(normalizeArabicDigits(trimmed), 10);
      if (!trimmed || isNaN(yr) || yr < 1990 || yr > maxYear) {
        return `اكتب سنة صح (من 1990 لـ ${maxYear})`;
      }
      return undefined;
    }

    case 'academic_grade':
      if (!allValues.education_level) return undefined;
      if (!trimmed || trimmed.length < 1 || trimmed.length > 50) {
        return 'اكتب تقديرك أو مجموعك';
      }
      return undefined;

    case 'preferred_study_field':
      if (!trimmed || trimmed.length < 2 || trimmed.length > 150) {
        return 'اكتب التخصص اللي حابب تدرسه';
      }
      return undefined;

    case 'motivation':
      if (!trimmed || trimmed.length < 60) {
        return 'احكيلنا ليه تستحق المنحة (60 حرف على الأقل)';
      }
      if (trimmed.length > 800) {
        return 'الحد الأقصى 800 حرف';
      }
      return undefined;

    case 'achievements':
      if (trimmed.length > 500) {
        return 'الحد الأقصى 500 حرف';
      }
      return undefined;

    default:
      return undefined;
  }
}

export function validateStep1(values: ScholarshipFormData): FormErrors {
  const errors: FormErrors = {};
  const fields: (keyof ScholarshipFormData)[] = [
    'full_name',
    'email',
    'phone',
    'date_of_birth',
    'governorate',
  ];

  for (const field of fields) {
    const err = validateField(field, values[field], values);
    if (err) errors[field] = err;
  }

  return errors;
}

export function validateStep2(values: ScholarshipFormData): FormErrors {
  const errors: FormErrors = {};

  const levelErr = validateField('education_level', values.education_level, values);
  if (levelErr) {
    errors.education_level = levelErr;
    return errors;
  }

  const isSchool =
    values.education_level === 'high_school_student' ||
    values.education_level === 'high_school_graduate';
  const isUniversity =
    values.education_level === 'university_student' ||
    values.education_level === 'university_graduate';

  if (isSchool) {
    const err = validateField('school_name', values.school_name, values);
    if (err) errors.school_name = err;
  }

  if (isUniversity) {
    const uniErr = validateField('university_name', values.university_name, values);
    if (uniErr) errors.university_name = uniErr;

    const facErr = validateField('faculty', values.faculty, values);
    if (facErr) errors.faculty = facErr;

    const majErr = validateField('major', values.major, values);
    if (majErr) errors.major = majErr;
  }

  const commonFields: (keyof ScholarshipFormData)[] = [
    'graduation_year',
    'academic_grade',
    'preferred_study_field',
    'motivation',
    'achievements',
  ];

  for (const field of commonFields) {
    const err = validateField(field, values[field], values);
    if (err) errors[field] = err;
  }

  return errors;
}

export function extractErrorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }

  if (Array.isArray(detail)) {
    for (const item of detail) {
      if (
        item !== null &&
        typeof item === 'object' &&
        'msg' in item &&
        typeof item.msg === 'string'
      ) {
        const trimmedMsg = item.msg.trim();
        if (trimmedMsg.length > 0) {
          return trimmedMsg;
        }
      }
    }
    return fallback;
  }

  return fallback;
}
